import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Sparkles, Users, Car, MapPin, Phone, ChevronRight, Route, Calendar, HandCoins, Award, Star, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { buildWhatsAppLink, telHref } from "../lib/whatsapp";
import { Reveal } from "../components/site/Reveal";
import { WhatsAppIcon } from "../components/site/Icons";
import BookingForm from "../components/site/BookingForm";
import Footer from "../components/site/Footer";
import SEO, { SITE } from "../components/site/SEO";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const HERO_IMG = "https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/0mmubo69_images%20%286%29.jpeg";

const TRUST = [
  { icon: ShieldCheck, title: "Reliable Service", body: "Punctual pickups, well-maintained cabs and a single point of contact for your trip." },
  { icon: Car, title: "Comfortable Travel", body: "Clean AC vehicles chosen to match your group size and comfort needs." },
  { icon: Users, title: "Experienced Drivers", body: "Drivers who know the Nallamala route, temple timings and local darshan flow." },
  { icon: Route, title: "Flexible Tour Packages", body: "One-day, round-trip and fully customized itineraries — plan it your way." },
  { icon: Sparkles, title: "Family-Friendly Trips", body: "Kid-safe stops, sattvic meal options and unhurried temple time." },
  { icon: Calendar, title: "Easy Booking", body: "Get a quote in minutes on WhatsApp or through our simple online form." },
];

const PACKAGES = [
  {
    slug: "srisailam-one-day-darshan-yatra",
    title: "Hyderabad to Srisailam One Day Tour",
    desc: "Same-day Jyotirlinga darshan with a Hyderabad pickup and return.",
    highlights: ["Mallikarjuna Jyotirlinga darshan", "Ropeway & boating", "Sakshi Ganapathi"],
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/0mmubo69_images%20%286%29.jpeg",
    alt: "Srisailam temple tour from Hyderabad",
  },
  {
    slug: "srisailam-two-day-temple-nallamala-yatra",
    title: "Hyderabad to Srisailam Round Trip (2 Days)",
    desc: "Temple, Nallamala forest, Patalaganga and Akka Mahadevi caves over 2 days.",
    highlights: ["Akka Mahadevi caves", "Istakameshwari darshan", "Overnight stay near temple"],
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/ovq5nlsj_images%20%288%29.jpeg",
    alt: "Srisailam round trip cab from Hyderabad",
  },
  {
    slug: null, title: "Srisailam Temple Tour",
    desc: "Focused temple visit covering Mallikarjuna Swamy and Bhramaramba Devi temples.",
    highlights: ["Mallikarjuna Swamy", "Bhramaramba Devi", "Prasadam"],
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/zjdxr0t0_images%20%2813%29.jpeg",
    alt: "Srisailam temple tour package",
  },
  {
    slug: null, title: "Srisailam Sightseeing Tour",
    desc: "Explore the dam, viewpoints, Patalaganga and the Nallamala forest scenery.",
    highlights: ["Srisailam Dam", "Octopus Viewpoint", "Patalaganga"],
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/14i70ux5_images%20%282%29.jpeg",
    alt: "Srisailam sightseeing tour",
  },
  {
    slug: null, title: "Customized Srisailam Tour",
    desc: "Design your own itinerary — pickup point, vehicle, stays and stops on your terms.",
    highlights: ["Flexible pickup", "Vehicle of choice", "Family or group"],
    img: "https://images.unsplash.com/photo-1516540268055-e1042f0b2c60?w=1200",
    alt: "Customized Srisailam tour from Hyderabad",
  },
];

const TEMPLES = [
  { name: "Mallikarjuna Swamy Temple", desc: "One of the twelve sacred Jyotirlingas of Shiva.", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/0mmubo69_images%20%286%29.jpeg" },
  { name: "Bhramaramba Devi Temple", desc: "One of the eighteen Shakti Peethas.", img: "https://images.unsplash.com/photo-1677434654722-69e7ba88b4ef?w=800" },
  { name: "Sakshi Ganapati Temple", desc: "The witness who records every pilgrim&apos;s darshan.", img: "https://images.unsplash.com/photo-1666694051761-cd972857da30?w=800" },
  { name: "Pathala Ganga", desc: "Sacred Krishna river ghat below the temple.", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/zjdxr0t0_images%20%2813%29.jpeg" },
];

const SIGHTS = [
  { name: "Srisailam Temple", desc: "Ancient Mallikarjuna Jyotirlinga complex.", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/0mmubo69_images%20%286%29.jpeg" },
  { name: "Srisailam Dam", desc: "Massive dam across the Krishna river.", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/zjdxr0t0_images%20%2813%29.jpeg" },
  { name: "Pathala Ganga", desc: "Cable car / stepped descent to the river.", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/14i70ux5_images%20%282%29.jpeg" },
  { name: "Sakshi Ganapati", desc: "A short drive from the main temple.", img: "https://images.unsplash.com/photo-1666694051761-cd972857da30?w=800" },
  { name: "Octopus View Point", desc: "Panoramic view over the Nallamala hills.", img: "https://images.unsplash.com/photo-1516540268055-e1042f0b2c60?w=800" },
  { name: "Akka Mahadevi Caves", desc: "Ancient meditation caves reached by boat.", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/ovq5nlsj_images%20%288%29.jpeg" },
];

const STEPS = [
  { n: "01", title: "Choose Your Trip", body: "Pick a one-day tour, round trip or a custom itinerary." },
  { n: "02", title: "Select Your Vehicle", body: "Sedan, SUV or Tempo Traveller — sized for your group." },
  { n: "03", title: "Confirm Your Booking", body: "Get a WhatsApp confirmation with pickup time and driver." },
  { n: "04", title: "Enjoy Your Journey", body: "Sit back — we handle the route, darshan and stops." },
];

const VEHICLES = [
  { name: "Sedan", suits: "Couples & small families", seats: "4 passengers", img: "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800" },
  { name: "SUV", suits: "Families up to 6", seats: "6 passengers", img: "https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=800" },
  { name: "Premium SUV (Innova Crysta)", suits: "Comfortable long-distance", seats: "6–7 passengers", img: "https://images.unsplash.com/photo-1502877338535-766e1452684a?w=800" },
  { name: "Tempo Traveller", suits: "Groups & extended families", seats: "12+ passengers", img: "https://images.unsplash.com/photo-1600661653561-629509216228?w=800" },
];

const FAQ = [
  { q: "How can I book a Hyderabad to Srisailam cab?", a: "Fill the quick booking form on this page or message us directly on WhatsApp. We'll reply with a quote and confirm your pickup." },
  { q: "Do you provide one-day Srisailam tour packages?", a: "Yes. Our one-day tour departs early from Hyderabad, covers Mallikarjuna Jyotirlinga darshan and returns the same night." },
  { q: "Can I book a round-trip cab from Hyderabad to Srisailam?", a: "Yes, we offer both one-way and round-trip cab services with flexible pickup points." },
  { q: "Can I customize my Srisailam sightseeing trip?", a: "Absolutely. Pick your own stops, stay duration and vehicle — we'll design the itinerary." },
  { q: "Which places can I visit during a Srisailam tour?", a: "Mallikarjuna Jyotirlinga, Bhramaramba Devi, Sakshi Ganapati, Pathala Ganga, Srisailam Dam, Akka Mahadevi Caves and Nallamala viewpoints." },
  { q: "How can I contact RSK Tours?", a: "Call or WhatsApp us using the buttons on the site, or use the enquiry form and we'll get back to you." },
];

export default function Home() {
  const { settings } = useSettings();
  const [trips, setTrips] = useState([]);

  useEffect(() => { api.get("/trips").then((r) => setTrips(r.data)).catch(() => {}); }, []);

  const wa = buildWhatsAppLink();
  const phone = "+916302206106";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "TravelAgency",
        name: "RSK Tours",
        url: SITE + "/",
        description: "Hyderabad to Srisailam tour packages and cab services.",
        areaServed: ["Hyderabad", "Srisailam", "Andhra Pradesh", "Telangana"],
        telephone: settings?.phone,
        email: settings?.email,
      },
      {
        "@type": "WebSite",
        name: "RSK Tours",
        url: SITE + "/",
        potentialAction: { "@type": "SearchAction", target: SITE + "/?q={search_term_string}", "query-input": "required name=search_term_string" },
      },
    ],
  };

  return (
    <>
      <SEO title="RSK Tours | Hyderabad to Srisailam Tours & Cab Services"
        description="RSK Tours offers Hyderabad to Srisailam cab services, one-day Srisailam tour packages, temple tours, sightseeing and customized trips from Hyderabad."
        path="/" image={HERO_IMG} jsonLd={jsonLd} />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-navy-deep" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Hyderabad to Srisailam temple tour" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/70 via-navy-deep/40 to-navy-deep/85" />
        </div>
        <div className="relative w-full max-w-[1400px] mx-auto px-4 sm:px-8 pt-28 pb-16">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7 }}
            className="text-gold text-xs sm:text-sm uppercase tracking-[0.25em] mb-5 flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Hyderabad · Srisailam
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.9 }}
            className="font-heading text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
            Hyderabad to Srisailam Tour Packages
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.9 }}
            className="mt-6 text-white/80 text-base sm:text-lg leading-relaxed max-w-2xl">
            Travel from Hyderabad to Srisailam comfortably with RSK Tours. Book reliable cab services, one-day tours, temple visits and sightseeing packages tailored for families, couples and groups.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-3">
            <a href="#booking" data-testid="hero-cta-book"
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-navy-deep font-medium px-7 h-14 rounded-full text-base transition-colors">
              Book Your Srisailam Tour <ArrowRight className="w-4 h-4" />
            </a>
            {phone && <a href={telHref} data-testid="hero-cta-call"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white px-7 h-14 rounded-full text-base transition-colors">
              <Phone className="w-5 h-5" /> Call Now
            </a>}
            <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1eae55] text-white px-7 h-14 rounded-full text-base transition-colors">
              <WhatsAppIcon className="w-5 h-5" /> WhatsApp Us
            </a>
          </motion.div>
        </div>
      </section>

      {/* BOOKING */}
      <section id="booking" className="relative -mt-16 sm:-mt-20 z-10 px-4 sm:px-8 mb-20">
        <div className="max-w-5xl mx-auto">
          <BookingForm />
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 py-16 sm:py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Why RSK Tours</p>
          <h2 className="text-navy text-3xl sm:text-5xl max-w-3xl leading-tight">Why Choose RSK Tours?</h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TRUST.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.05}>
              <div className="bg-white rounded-2xl border border-border p-7 h-full shadow-card hover:shadow-cardHover transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-gold-soft flex items-center justify-center mb-5">
                  <t.icon className="w-6 h-6 text-navy" />
                </div>
                <h3 className="text-2xl text-navy mb-2">{t.title}</h3>
                <p className="text-muted2 leading-relaxed">{t.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HYD → SRI SEO */}
      <section className="bg-navy text-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Hyderabad to Srisailam</p>
            <h2 className="text-4xl sm:text-5xl leading-tight">Hyderabad to Srisailam Cab & Tour Services</h2>
            <p className="mt-6 text-white/75 leading-relaxed text-base sm:text-lg max-w-xl">
              RSK Tours provides convenient travel from Hyderabad to Srisailam for temple visits, sightseeing, family trips and customized tours. Choose a cab service or tour package based on your travel requirements and schedule.
            </p>
            <a href="#booking" data-testid="hyd-cta"
              className="mt-8 inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-navy-deep font-medium px-7 h-13 py-3.5 rounded-full transition-colors">
              Plan My Srisailam Trip <ArrowRight className="w-4 h-4" />
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden">
              <img src="https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/zjdxr0t0_images%20%2813%29.jpeg"
                alt="Krishna river Nallamala hills Srisailam" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* PACKAGES */}
      <section id="packages" className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20 sm:py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Packages</p>
          <h2 className="text-navy text-4xl sm:text-5xl">Popular Srisailam Tour Packages</h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.title} delay={(i % 3) * 0.08}>
              <article className="group bg-white border border-border rounded-2xl overflow-hidden shadow-card hover:shadow-cardHover transition-shadow h-full flex flex-col" data-testid={`package-card-${i}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.img} alt={p.alt || p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-2xl text-navy leading-tight">{p.title}</h3>
                  <p className="mt-2 text-muted2 text-sm">{p.desc}</p>
                  <ul className="mt-4 space-y-1.5">
                    {p.highlights.map((h) => (
                      <li key={h} className="flex items-center gap-2 text-sm text-navy/80"><ChevronRight className="w-4 h-4 text-gold" />{h}</li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-5 border-t border-border flex items-center justify-between gap-3">
                    <span className="text-navy font-medium">Get Quote</span>
                    <div className="flex items-center gap-2">
                      {p.slug ? (
                        <Link to={`/trips/${p.slug}`} className="text-sm text-navy hover:text-gold inline-flex items-center gap-1">View Details<ArrowRight className="w-3.5 h-3.5" /></Link>
                      ) : (
                        <Link to="/srisailam-tour-packages" className="text-sm text-navy hover:text-gold inline-flex items-center gap-1">View Details<ArrowRight className="w-3.5 h-3.5" /></Link>
                      )}
                      <a href="#booking" className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-deep text-white text-sm px-4 py-2 rounded-full">Book Now</a>
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TEMPLES */}
      <section className="bg-bone">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Divine</p>
            <h2 className="text-navy text-4xl sm:text-5xl mb-3">Visit the Sacred Temples of Srisailam</h2>
            <p className="max-w-2xl text-muted2 leading-relaxed">RSK Tours plans your temple darshan across Srisailam&apos;s most revered shrines — with unhurried pacing and local guidance.</p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TEMPLES.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.06}>
                <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-card">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={t.img} alt={`${t.name} — Srisailam temple tour`} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5">
                    <h3 className="text-xl text-navy leading-tight">{t.name}</h3>
                    <p className="mt-2 text-sm text-muted2">{t.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <a href="#booking" data-testid="temples-cta" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white px-7 h-12 rounded-full">Explore Srisailam Tour <ArrowRight className="w-4 h-4" /></a>
          </div>
        </div>
      </section>

      {/* SIGHTSEEING */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Sightseeing</p>
          <h2 className="text-navy text-4xl sm:text-5xl">Popular Places to Visit in Srisailam</h2>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SIGHTS.map((s, i) => (
            <Reveal key={s.name} delay={(i % 3) * 0.05}>
              <div className="group relative rounded-2xl overflow-hidden border border-border shadow-card">
                <img src={s.img} alt={`${s.name} — Srisailam sightseeing`} loading="lazy" className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/85 via-navy-deep/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                  <h3 className="text-2xl leading-tight">{s.name}</h3>
                  <p className="text-white/75 text-sm mt-1">{s.desc}</p>
                  <a href="#booking" className="mt-3 inline-flex items-center gap-1.5 text-gold text-sm hover:underline">View Tour <ArrowRight className="w-3.5 h-3.5" /></a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-navy text-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Simple</p>
            <h2 className="text-white text-4xl sm:text-5xl">Book Your Srisailam Trip in 4 Easy Steps</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="border-t border-white/20 pt-6">
                  <span className="text-gold font-heading text-5xl">{s.n}</span>
                  <h3 className="mt-3 text-2xl">{s.title}</h3>
                  <p className="mt-2 text-white/70 leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VEHICLES */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Fleet</p>
          <h2 className="text-navy text-4xl sm:text-5xl">Choose Your Travel Vehicle</h2>
          <p className="mt-4 text-muted2 max-w-2xl">Pick a vehicle to match your group size. Final vehicle assignment is confirmed after your booking.</p>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {VEHICLES.map((v, i) => (
            <Reveal key={v.name} delay={(i % 4) * 0.06}>
              <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-card h-full flex flex-col">
                <div className="aspect-[4/3] bg-bone overflow-hidden">
                  <img src={v.img} alt={`${v.name} — RSK Tours vehicle`} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="text-xl text-navy">{v.name}</h3>
                  <p className="text-sm text-muted2 mt-1">{v.seats}</p>
                  <p className="text-sm text-navy/80 mt-3">Suitable for: {v.suits}</p>
                  <a href="#booking" data-testid={`vehicle-book-${i}`} className="mt-4 inline-flex items-center justify-center gap-1.5 bg-navy hover:bg-navy-deep text-white text-sm h-10 rounded-full">Book This Vehicle</a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-xs text-muted2 max-w-2xl">Vehicle photos are indicative. Final make and model is confirmed with your booking.</p>
      </section>

      {/* WHY */}
      <section className="bg-bone">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Guide</p>
            <h2 className="text-navy text-4xl sm:text-5xl leading-tight">Srisailam Tour from Hyderabad</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-5 text-muted2 text-base sm:text-lg leading-relaxed">
              <p>Start early from your Hyderabad pickup point and enjoy a comfortable road journey through the Nallamala hills. Once you reach Srisailam, our team assists with your Mallikarjuna Jyotirlinga and Bhramaramba Devi darshan.</p>
              <p>Add-ons include Sakshi Ganapati, Pathala Ganga, the Srisailam Dam and scenic Nallamala viewpoints. On round-trip and 2-day itineraries, we can also arrange the Akka Mahadevi Caves boat visit.</p>
              <p>Whether you are travelling as a family, a couple or a group, we can shape the schedule around your temple timings and comfort — from a single-day dash to a slow, restful weekend.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* REVIEWS placeholder */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Reviews</p>
          <h2 className="text-navy text-4xl sm:text-5xl">What Our Customers Say</h2>
        </Reveal>
        <Reveal>
          <div className="mt-10 bg-white border border-dashed border-border rounded-2xl p-10 text-center">
            <Star className="w-8 h-8 text-gold mx-auto" />
            <p className="mt-4 text-navy text-lg">Reviews will appear here as our recent travellers share their experience.</p>
            <p className="mt-2 text-muted2 text-sm">Travelled with us? Send a note on WhatsApp — we&apos;d love to feature you here.</p>
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section className="bg-bone">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-20">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">FAQ</p>
            <h2 className="text-navy text-4xl sm:text-5xl">Frequently Asked Questions</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`}>
                  <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left text-navy text-lg">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted2 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden">
        <img src="https://customer-assets-rejwkqb3.emergentagent.net/job_holy-wanderer/artifacts/ovq5nlsj_images%20%288%29.jpeg" alt="Srisailam pilgrimage" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-navy-deep/85" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-8 py-24 text-center text-white">
          <h2 className="text-4xl sm:text-6xl leading-tight">Ready for Your Srisailam Trip?</h2>
          <p className="mt-5 text-white/80 text-lg">Plan your Hyderabad to Srisailam journey with RSK Tours.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href="#booking" data-testid="final-book" className="inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-navy-deep font-medium px-7 h-13 py-3.5 rounded-full">Book Your Tour <ArrowRight className="w-4 h-4" /></a>
            {phone && <a href={telHref} data-testid="final-call" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white px-7 py-3.5 rounded-full"><Phone className="w-4 h-4" /> Call Now</a>}
            <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="final-whatsapp" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1eae55] text-white px-7 py-3.5 rounded-full"><WhatsAppIcon className="w-4 h-4" /> WhatsApp Us</a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
